import { formatMoney, type OrderStatus, queryKeys } from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/** The order fields a transition actually needs — enough to drive the dialog
 * from a full order or from a lighter list row alike. */
export interface TransitionTarget {
  id: string
  orderNumber: string
  totalCents: number
  /** Pre-fills the tracking field when marking shipped — set once a label
   *  has been bought (t0800), so staff don't retype what buying it already
   *  recorded. Absent on a lighter list row, where it simply starts blank. */
  trackingNumber?: string | null
}

export function actionLabel(status: OrderStatus): string {
  return (
    {
      pending_payment: 'Send for payment',
      paid: 'Take payment',
      fulfilling: 'Start preparing',
      shipped: 'Mark shipped',
      ready_for_pickup: 'Ready to collect',
      completed: 'Complete',
      cancelled: 'Cancel',
      refunded: 'Refund',
      draft: 'Back to draft',
    } satisfies Record<OrderStatus, string>
  )[status]
}

/**
 * Confirms a move, and collects a payment when the move needs one.
 *
 * The payment fields appear only for `paid`, and the amount is fixed to the
 * balance: the API rejects anything else, and a free-text amount here would
 * only invite a mismatch it will refuse.
 *
 * `via` supports a shortcut the state machine doesn't offer directly — e.g. a
 * `paid` order has no single move straight to `completed` (it must pass
 * through `fulfilling` first). When set, the dialog posts `via` first and
 * `to` second, so the one button a counter needs still needs one click.
 */
export function OrderTransitionDialog({
  order,
  to,
  via,
  onClose,
}: {
  order: TransitionTarget
  to: OrderStatus | null
  via?: OrderStatus | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState('cash')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [trackingNumber, setTrackingNumber] = useState('')

  // What is left to collect once any gift card / store credit held against
  // the order is taken off (t1102) — the API only accepts exactly that. A
  // lighter list row doesn't carry it, so it is read from the order itself.
  const { data: current } = useQuery({
    queryKey: queryKeys.orders.detail(order.id),
    queryFn: () => api.get<{ balanceDueCents: number }>(`/orders/${order.id}`),
    enabled: to === 'paid',
  })
  const amountDueCents = current?.balanceDueCents ?? order.totalCents

  // Pre-fills from whatever buying a label already recorded (t0800) — staff
  // marking an order shipped after printing its label shouldn't have to
  // retype the number that purchase produced.
  useEffect(() => {
    if (to === 'shipped') setTrackingNumber(order.trackingNumber ?? '')
  }, [to, order.trackingNumber])

  const move = useMutation({
    mutationFn: async () => {
      if (via) {
        await api.post(`/orders/${order.id}/transition`, { to: via, note: null, payment: null })
      }
      return api.post(`/orders/${order.id}/transition`, {
        to,
        note: note.trim() || null,
        payment:
          to === 'paid'
            ? {
                provider,
                amountCents: amountDueCents,
                reference: reference.trim() || null,
              }
            : null,
        trackingNumber: to === 'shipped' ? trackingNumber.trim() || null : null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() })
      toast.success(`${order.orderNumber} is now ${humanizeStatus(to ?? '').toLowerCase()}`)
      setNote('')
      setReference('')
      setTrackingNumber('')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const takesPayment = to === 'paid'
  const isShipping = to === 'shipped'
  const isDestructive = to === 'cancelled' || to === 'refunded'

  return (
    <Dialog open={to !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {to ? actionLabel(to) : ''} — {order.orderNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isDestructive ? (
            <Alert tone="warning">
              {to === 'cancelled'
                ? 'Anything reserved for this order goes back on the shelf.'
                : 'The money is marked as returned, and any device comes back for re-provisioning before it can be sold again.'}
            </Alert>
          ) : null}

          {takesPayment ? (
            <>
              <Field label="Amount">
                {(props) => (
                  <Input {...props} value={formatMoney(amountDueCents)} readOnly disabled />
                )}
              </Field>
              <Field label="How was it paid?">
                {(props) => (
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="external_card">Card machine</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Reference" hint="Card slip or receipt number, if there is one">
                {(props) => (
                  <Input
                    {...props}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : null}

          {isShipping ? (
            <Field label="Tracking number" hint="Optional — the carrier reference for this parcel">
              {(props) => (
                <Input
                  {...props}
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
              )}
            </Field>
          ) : null}

          <Field label="Note" hint="Kept on the order history. The customer does not see it.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={() => move.mutate()}
            disabled={move.isPending}
          >
            {move.isPending ? 'Working…' : to ? actionLabel(to) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
